import React, { useEffect } from 'react';
import { Button, Card, Typography, Space } from 'antd';
import { GoogleOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { checkGoogleAuthStatus, initiateGoogleAuth } from '../../store/slices/googleBusinessSlice';
import { showNotification, NotificationType } from '../../utils';

const { Text, Paragraph } = Typography;

const GoogleAuthCard = ({ collapsed }) => {
  const dispatch = useDispatch();
  const { isAuthorized, authLoading, lastSync } = useSelector((state) => state.googleBusiness);

  useEffect(() => {
    dispatch(checkGoogleAuthStatus());
  }, [dispatch]);

  const handleGoogleAuth = async () => {
    try {
      const result = await dispatch(initiateGoogleAuth()).unwrap();

      console.log('Google Auth URL:', result);

      if (result?.data?.authUrl) {
        // Open Google OAuth in a popup window
        const width = 800;
        const height = 800;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const authWindow = window.open(result.data.authUrl, 'Google Business Authorization', `width=${width},height=${height},left=${left},top=${top}`);

        // Poll for window close or message
        const pollTimer = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(pollTimer);
            dispatch(checkGoogleAuthStatus());
          }
        }, 500);

        // Listen for messages from popup
        const messageHandler = (event) => {
          if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
            clearInterval(pollTimer);
            authWindow.close();
            showNotification('Successfully authorized Google Business!', NotificationType.SUCCESS);
            dispatch(checkGoogleAuthStatus());
            window.removeEventListener('message', messageHandler);
          } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
            clearInterval(pollTimer);
            authWindow.close();
            showNotification(event.data.message || 'Authorization failed', NotificationType.ERROR);
            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);
      }
    } catch (error) {
      showNotification('Failed to initiate Google authorization', NotificationType.ERROR);
      console.error('Google auth error:', error);
    }
  };

  if (collapsed) {
    return null;
  }

  return (
    <div
      style={{
        padding: '12px 16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Card
        size="small"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '8px',
          border: 'none',
        }}
        bodyStyle={{ padding: '12px' }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {authLoading ? (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <SyncOutlined spin style={{ fontSize: '18px', color: '#667eea' }} />
            </div>
          ) : isAuthorized ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                  Google Business Connected
                </Text>
              </div>
              <Paragraph
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: '#8c8c8c',
                }}
              >
                Your account is authorized. Reviews will be synced automatically.
              </Paragraph>
              {lastSync && <Text style={{ fontSize: '11px', color: '#bfbfbf' }}>Last synced: {new Date(lastSync).toLocaleString()}</Text>}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <GoogleOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                  Google Business Reviews
                </Text>
              </div>
              <Paragraph
                style={{
                  margin: 0,
                  marginBottom: '8px',
                  fontSize: '12px',
                  color: '#595959',
                  lineHeight: '1.4',
                }}
              >
                Connect your Google Business account to fetch and manage customer reviews automatically.
              </Paragraph>
              <Button
                type="primary"
                icon={<GoogleOutlined />}
                loading={authLoading}
                onClick={handleGoogleAuth}
                block
                style={{
                  background: '#667eea',
                  borderColor: '#667eea',
                  fontWeight: 500,
                }}
              >
                Authorize Google
              </Button>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default GoogleAuthCard;
